import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GraphQLResult } from 'aws-amplify/api';
import { CountMapUtils } from '@/utils/CountMapUtils';
import log from 'loglevel';
import { ListService } from '@/service/list/ListService';
import { FilterCountCalculator } from '@/helper/FilterCountCalculator';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import { orderFilterSelections } from '@/utils/filter';
import {
  FilterRowSelection,
  FilterSelections,
  RelationalOperator,
} from '@lambda-layer/types/filter';
import { buildDefaultFilterSelections } from '@lambda-layer/utils/commonUtils';
import { ExclusionRule } from '@/API.ts';
import { useFilterValues } from '@/hooks/useFilterValues.ts';

/**
 * Returns a record with each filter definition's ID as the key,
 * and `initialValue` as the value.
 */
const initializeState = <T>(
  filterDefinitions: FilterDefinition[],
  initialValue: T
): Record<string, T> =>
  filterDefinitions.reduce(
    (acc, field) => {
      acc[field.id] = initialValue;
      return acc;
    },
    {} as Record<string, T>
  );

/**
 * Returns the filter selections up to (and optionally including) `fieldId`
 * from a set of ordered selections. Helpful for partial queries.
 */
const getPriorSelections = (
  orderedSelections: FilterSelections,
  fieldId: string,
  inclusive = false
): FilterSelections => {
  const result: FilterSelections = {};
  for (const key of Object.keys(orderedSelections)) {
    if (key === fieldId) {
      if (inclusive) {
        result[key] = orderedSelections[key];
      }
      break;
    }
    result[key] = orderedSelections[key];
  }
  return result;
};

/**
 * Manages filter selections and count retrieval for a given set of filter definitions
 * in conjunction with a ListService.
 */
const useFilterSelections = (
  listService: ListService<unknown>,
  filterDefinitions: FilterDefinition[],
  listType?: string,
  exclusions: ExclusionRule[] = []
) => {
  log.debug(
    `useFilterSelections initialized with ${filterDefinitions.length} fields and ${exclusions.length} exclusions`
  );

  // Initialize default filters hook
  const defaultFilters = useFilterValues(listType || '');

  // State to track if database defaults have been applied
  const [databaseDefaultsApplied, setDatabaseDefaultsApplied] = useState(false);
  // Use a ref to track if we're currently applying defaults to prevent multiple concurrent executions
  const applyingDefaultsRef = useRef(false);

  // Waiters that should resolve once database defaults have been applied
  const databaseDefaultsWaitersRef = useRef<Array<() => void>>([]);
  const waitForDatabaseDefaults = useCallback(async () => {
    // If no listType or already applied, nothing to wait for
    if (!listType || databaseDefaultsApplied) return;
    await new Promise<void>(resolve => {
      databaseDefaultsWaitersRef.current.push(resolve);
    });
  }, [listType, databaseDefaultsApplied]);

  // Create a quick-lookup map from field ID to FilterDefinition
  const fieldMap = useMemo(
    () =>
      filterDefinitions.reduce(
        (map, field) => {
          map[field.id] = field;
          return map;
        },
        {} as Record<string, FilterDefinition>
      ),
    [filterDefinitions]
  );

  // State for tracking the user's current filter selections
  const [filterSelections, setFilterSelections] = useState<FilterSelections>(
    buildDefaultFilterSelections(filterDefinitions)
  );
  // Keep a ref in sync to easily reference the current filter selections within async callbacks
  const filterSelectionsRef = useRef(filterSelections);
  useEffect(() => {
    filterSelectionsRef.current = filterSelections;
  }, [filterSelections]);

  // Each filter field ID maps to a Map<value, count> for storing count data
  const [fieldSelectionCountMap, setFieldSelectionCountMap] = useState<
    Record<string, Map<string | null, number>>
  >(initializeState(filterDefinitions, new Map<string, number>()));

  // Loading indicators by field ID
  const [loading, setLoading] = useState<Record<string, boolean>>(
    initializeState(filterDefinitions, false)
  );

  // Global error handling for fetches
  const [error, setError] = useState<string | null>(null);

  // Fields for which counts have been successfully loaded at least once
  const loadedFiltersRef = useRef<Set<string>>(new Set());

  // Request ID tracking to prevent race conditions
  const requestIdMapRef = useRef<Record<string, number>>({});

  /**
   * Fetches counts for a given database field and updates state accordingly.
   */
  const fetchAndUpdateCounts = useCallback(
    async (fieldId: string, providedSelections?: FilterSelections) => {
      const field = fieldMap[fieldId];
      if (!field) {
        log.error(`Field ${fieldId} not found in fieldMap`);
        return;
      }

      // Ensure database-driven defaults are applied before first fetches
      if (listType) {
        await waitForDatabaseDefaults();
      }

      // Skip fetching for non-database fields; they may have defaultOptions instead
      if (!field.isDatabaseField) {
        if (field.defaultOptions) {
          log.debug(
            `Setting default drop-down options for non-database field ${fieldId}`
          );
          const counts = new Map<string, number>();
          field.defaultOptions.forEach(option =>
            counts.set(option?.toString() || '', 0)
          );

          // Apply exclusions even to non-database fields with default options
          const filteredCounts = removeExcludedValuesFromCounts(
            counts,
            exclusions,
            field.databaseMetadata?.columnName || field.label || fieldId
          );

          setFieldSelectionCountMap(prev => ({
            ...prev,
            [fieldId]: filteredCounts,
          }));
        }
        log.debug(`Skipping count fetch for non-database field ${fieldId}`);
        return;
      }

      // Bump the request ID for this field to detect stale responses
      requestIdMapRef.current[fieldId] =
        (requestIdMapRef.current[fieldId] || 0) + 1;
      const currentRequestId = requestIdMapRef.current[fieldId];

      setLoading(prev => ({ ...prev, [fieldId]: true }));

      log.debug(
        `Fetching counts for database field ${fieldId}, exclusions:`,
        exclusions
      );
      // If this is an array field change, run aggregate query to update both total and individual bucket counts
      if (field.isArrayField && providedSelections) {
        try {
          // For array fields, exclude the current field from the filter selections
          // to maintain all available options in the dropdown
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [fieldId]: _, ...selectionsExcludingCurrentField } =
            providedSelections;

          // Use aggregate query to get individual flag counts, not just total
          const countMap = await listService.getFilterCountMap(
            fieldId,
            filterDefinitions,
            selectionsExcludingCurrentField
          );

          // Also get the record count that includes the current field's selection
          // This will be used for the field's record count display
          const recordCountWithCurrentField =
            await listService.getFilterCountMap(
              fieldId,
              filterDefinitions,
              providedSelections
            );

          // If our request ID has changed, this response is stale
          if (currentRequestId !== requestIdMapRef.current[fieldId]) {
            log.debug(`Stale response for array field ${fieldId} discarded.`);
            return;
          }

          // Use the total count from the query that includes current field selection
          const totalRecordCount = CountMapUtils.getTotalCount(
            recordCountWithCurrentField
          );

          // Update the count map with the correct total record count
          CountMapUtils.setTotalCount(countMap, totalRecordCount);

          // Preserve selected options that might not appear in the new count results
          const currentSelections = filterSelectionsRef.current[fieldId];
          if (currentSelections?.value?.length) {
            // Ensure all currently selected values are preserved in the count map
            currentSelections.value.forEach(selectedValue => {
              // Convert selectedValue to string to match countMap key type
              const stringValue = selectedValue?.toString() || null;
              if (!countMap.has(stringValue)) {
                countMap.set(stringValue, 0);
              }
            });
          }

          setFieldSelectionCountMap(prev => ({ ...prev, [fieldId]: countMap }));
          loadedFiltersRef.current.add(fieldId);
        } catch (err) {
          log.error('Error fetching aggregate counts for array field:', err);
        } finally {
          // Only clear loading if this request is still current
          if (requestIdMapRef.current[fieldId] === currentRequestId) {
            setLoading(prev => ({ ...prev, [fieldId]: false }));
          }
        }
        return;
      }

      try {
        // Use provided selections or current selections (no need for applyExclusions anymore)
        const baseSelections =
          providedSelections ?? filterSelectionsRef.current;

        // Order the selections in the defined field order
        const orderedFilterSelections = orderFilterSelections(
          filterDefinitions,
          baseSelections
        );

        // Build prior selections for aggregate fields (exclude current field), include only previous filters
        const priorSelections = getPriorSelections(
          orderedFilterSelections,
          fieldId,
          !field.isAggregateField
        );

        // Pass exclusions to the service layer for repository-level filtering
        const counts = await listService.getFilterCountMap(
          fieldId,
          filterDefinitions,
          priorSelections,
          exclusions
        );

        log.debug(`Received counts for ${fieldId}:`, counts.size, 'items');

        // Exclusions are now handled at repository level - no client-side filtering needed

        // If our request ID has changed, this response is stale
        if (currentRequestId !== requestIdMapRef.current[fieldId]) {
          log.debug(`Stale response for field ${fieldId} discarded.`);
          return;
        }

        // Update our state with the counts (already filtered at repository level)
        setFieldSelectionCountMap(prev => ({
          ...prev,
          [fieldId]: counts,
        }));
        loadedFiltersRef.current.add(fieldId);
      } catch (err) {
        log.error('Error fetching filter counts:', err);
        const graphQLError = err as GraphQLResult;
        setError(
          graphQLError?.errors?.[0]?.message || 'Error fetching filter counts'
        );
      } finally {
        // Only clear loading if this request is still current
        if (requestIdMapRef.current[fieldId] === currentRequestId) {
          setLoading(prev => ({ ...prev, [fieldId]: false }));
        }
      }
    },
    [
      fieldMap,
      filterDefinitions,
      listService,
      exclusions,
      waitForDatabaseDefaults,
      listType,
    ]
  );

  /**
   * Updates the selection of one filter field in state, leaving others unchanged.
   */
  const updateFieldSelectionInState = useCallback(
    (fieldId: string, value: FilterRowSelection) => {
      setFilterSelections(prev => {
        const newSelections = { ...prev, [fieldId]: value };
        filterSelectionsRef.current = newSelections; // Keep the ref synced
        return newSelections;
      });
    },
    []
  );

  /**
   * Applies a new selection for `fieldId`, then triggers re-fetches for subsequent
   * database fields if needed. If a subsequent field has no selections,
   * its counts are reset to an empty map.
   */
  const updateSelection = useCallback(
    async (fieldId: string, selection: FilterRowSelection) => {
      const field = fieldMap[fieldId];
      if (!field) return;

      // Always update this field's selection in state
      updateFieldSelectionInState(fieldId, selection);

      // If it's not a database field, skip fetching counts
      if (!field.isDatabaseField) {
        log.debug(
          `Skipping selection update for non-database field ${fieldId}`
        );
        return;
      }

      log.debug(`Updating selection for database field ${fieldId}`);

      // Invalidate subsequent filters, since a parent changed
      const changedFilterIndex = filterDefinitions.findIndex(
        f => f.id === fieldId
      );
      const subsequentFilters = filterDefinitions.slice(changedFilterIndex + 1);
      // Remove them from loadedFiltersRef since their data might be stale
      subsequentFilters.forEach(sf => loadedFiltersRef.current.delete(sf.id));

      // Re-fetch or reset counts for subsequent database fields
      const nextSelections = filterSelectionsRef.current;
      for (const sf of subsequentFilters) {
        const sfSelection = nextSelections[sf.id];
        const sfIsDatabaseField = sf.csvMetadata?.fieldType === 'database';

        if (!sfIsDatabaseField) {
          continue;
        }

        const hasSelection = sfSelection?.value?.length > 0;

        if (hasSelection) {
          // If the child filter has a value, refresh its counts
          await fetchAndUpdateCounts(sf.id, nextSelections);
        } else {
          // If there's no selection, reset the counts
          setFieldSelectionCountMap(prev => ({
            ...prev,
            [sf.id]: new Map<string, number>(),
          }));
        }
      }
    },
    [
      fieldMap,
      filterDefinitions,
      fetchAndUpdateCounts,
      updateFieldSelectionInState,
    ]
  );

  /**
   * Computes overall counts per filter based on current state and the stored count maps.
   */
  const calculateFilterCounts = useCallback(() => {
    const calculator = new FilterCountCalculator(
      filterDefinitions,
      fieldSelectionCountMap,
      filterSelectionsRef.current
    );
    return calculator.calculateAllFilterCounts();
  }, [fieldSelectionCountMap, filterDefinitions]);

  /**
   * Called when a filter field value changes; if it's not an aggregate field,
   * we fetch updated counts for it immediately.
   */
  const handleChange = useCallback(
    async (
      fieldId: string,
      filterRowSelection: FilterRowSelection,
      providedSelections?: FilterSelections
    ) => {
      const field = fieldMap[fieldId];
      if (!field) return;

      // For non-aggregate fields or array fields, fetch updated counts right away
      if (!field.isAggregateField || field.isArrayField) {
        const combinedSelections = {
          ...(providedSelections ?? filterSelectionsRef.current),
          [fieldId]: filterRowSelection,
        };
        await fetchAndUpdateCounts(fieldId, combinedSelections).catch(err =>
          log.error(err)
        );
      }
    },
    [fieldMap, fetchAndUpdateCounts]
  );

  /**
   * Called when a filter field is focused, and may trigger an initial fetch if
   * data hasn't been loaded yet.
   */
  const handleFocusChange = useCallback(
    async (fieldId: string, providedSelections?: FilterSelections) => {
      const field = fieldMap[fieldId];
      if (!field) return;

      if (field.isAggregateField) {
        // Skip if we've already loaded counts or we're currently loading
        if (loadedFiltersRef.current.has(fieldId) || loading[fieldId]) {
          log.debug(
            `Filter counts for ${fieldId} already loaded or in progress`
          );
          return;
        }

        // If we haven't loaded counts yet, do it now (include providedSelections if passed)
        if (providedSelections || fieldSelectionCountMap[fieldId].size === 0) {
          await fetchAndUpdateCounts(fieldId, providedSelections).catch(err =>
            log.error(err)
          );
        }
      }
    },
    [fieldMap, loading, fieldSelectionCountMap, fetchAndUpdateCounts]
  );

  /**
   * Resets all selections to their default state and clears fetched counts.
   */
  const resetAllFilters = useCallback(() => {
    const defaultSelections = buildDefaultFilterSelections(filterDefinitions);
    setFilterSelections(defaultSelections);
    filterSelectionsRef.current = defaultSelections;

    setFieldSelectionCountMap(
      initializeState(filterDefinitions, new Map<string, number>())
    );
    setLoading(initializeState(filterDefinitions, false));

    loadedFiltersRef.current.clear();
    requestIdMapRef.current = {};
  }, [filterDefinitions]);

  /**
   * On mount (or when definitions change), if there are default filter values,
   * apply them and load counts as needed.
   */
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultsAppliedRef.current) return;

    const fieldsThatNeedDefaults = filterDefinitions.filter(
      field =>
        field.controlType &&
        field.defaultValue?.length &&
        fieldSelectionCountMap[field.id].size === 0
    );

    if (fieldsThatNeedDefaults.length > 0) {
      fieldsThatNeedDefaults.forEach(field => {
        log.debug(`Applying default value for field ${field.id}`);
        void handleFocusChange(field.id);
        void handleChange(field.id, {
          relationalOperator:
            field.defaultRelationalOperator ?? RelationalOperator.EQUAL,
          value: field.defaultValue || [],
        });
      });
    }

    defaultsAppliedRef.current = true;
  }, [
    filterDefinitions,
    fieldSelectionCountMap,
    handleFocusChange,
    handleChange,
  ]);

  /**
   * Apply database-driven default filter selections on mount
   */
  useEffect(() => {
    // Prevent multiple concurrent executions
    if (
      !listType ||
      databaseDefaultsApplied ||
      applyingDefaultsRef.current ||
      filterDefinitions.length === 0
    ) {
      return;
    }

    // Mark that we're applying defaults
    applyingDefaultsRef.current = true;

    const applyDatabaseDefaults = async () => {
      try {
        log.debug(`Loading database default filters for ${listType}`);

        // Generate default selections from database
        const databaseDefaults =
          await defaultFilters.generateDefaultSelections(filterDefinitions);

        if (Object.keys(databaseDefaults).length > 0) {
          log.debug(
            `Applying database defaults for ${listType}:`,
            databaseDefaults
          );

          // Merge database defaults with existing selections (database defaults take precedence)
          const mergedSelections = {
            ...filterSelectionsRef.current,
            ...databaseDefaults,
          };

          setFilterSelections(mergedSelections);
          filterSelectionsRef.current = mergedSelections;

          // Only trigger one API call per field that has database defaults
          // Process them sequentially to avoid race conditions
          const fieldsToUpdate = Object.keys(databaseDefaults).filter(
            fieldId => {
              const field = fieldMap[fieldId];
              return (
                field?.isDatabaseField &&
                databaseDefaults[fieldId].value.length > 0
              );
            }
          );

          for (const fieldId of fieldsToUpdate) {
            const field = fieldMap[fieldId];
            if (field?.isDatabaseField) {
              // Bump request ID and set loading
              requestIdMapRef.current[fieldId] =
                (requestIdMapRef.current[fieldId] || 0) + 1;
              const currentRequestId = requestIdMapRef.current[fieldId];
              setLoading(prev => ({ ...prev, [fieldId]: true }));

              try {
                const counts = await listService.getFilterCountMap(
                  fieldId,
                  filterDefinitions,
                  mergedSelections,
                  exclusions
                );

                // Discard stale response
                if (currentRequestId !== requestIdMapRef.current[fieldId]) {
                  log.debug(
                    `Stale response during defaults application for ${fieldId} discarded.`
                  );
                } else {
                  setFieldSelectionCountMap(prev => ({
                    ...prev,
                    [fieldId]: counts,
                  }));
                  loadedFiltersRef.current.add(fieldId);
                }
              } catch (err) {
                log.error(
                  'Error fetching counts during default application:',
                  err
                );
              } finally {
                if (requestIdMapRef.current[fieldId] === currentRequestId) {
                  setLoading(prev => ({ ...prev, [fieldId]: false }));
                }
              }
            }
          }
        }

        setDatabaseDefaultsApplied(true);
      } catch (error) {
        log.error('Failed to apply database default filters:', error);
        setDatabaseDefaultsApplied(true); // Mark as applied to prevent retry loops
      } finally {
        // Resolve any waiters now that defaults have been processed
        if (databaseDefaultsWaitersRef.current.length) {
          const waiters = [...databaseDefaultsWaitersRef.current];
          databaseDefaultsWaitersRef.current = [];
          waiters.forEach(resolve => resolve());
        }
        // Always reset the flag when done
        applyingDefaultsRef.current = false;
      }
    };

    void applyDatabaseDefaults();
  }, [
    listType,
    databaseDefaultsApplied,
    defaultFilters,
    exclusions,
    fieldMap,
    filterDefinitions,
    listService,
  ]);

  // Also resolve waiters if databaseDefaultsApplied becomes true by any other path
  useEffect(() => {
    if (!listType || databaseDefaultsApplied) {
      if (databaseDefaultsWaitersRef.current.length) {
        const waiters = [...databaseDefaultsWaitersRef.current];
        databaseDefaultsWaitersRef.current = [];
        waiters.forEach(resolve => resolve());
      }
    }
  }, [listType, databaseDefaultsApplied]);

  /**
   * Clears the global error after five seconds.
   */
  useEffect(() => {
    if (error) {
      const timeout = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timeout);
    }
  }, [error]);

  return {
    filterSelections,
    fieldSelectionCountMap,
    error,
    loading,
    updateSelection,
    handleFocusChange,
    handleChange,
    fetchAndUpdateCounts,
    calculateFilterCounts,
    resetAllFilters,
  };
};

export default useFilterSelections;

/**
 * Helper to remove excluded values from dropdown options
 */
function removeExcludedValuesFromCounts(
  counts: Map<string | null, number>,
  exclusions: ExclusionRule[],
  fieldName: string
): Map<string | null, number> {
  const filteredCounts = new Map(counts);

  log.debug(
    `removeExcludedValuesFromCounts: fieldName=${fieldName}, exclusions count=${exclusions.length}`
  );

  const applicableExclusions = exclusions.filter(
    rule => rule.fieldName === fieldName && rule.operator === 'NOT_IN'
  );

  log.debug(`Applicable exclusions for ${fieldName}:`, applicableExclusions);

  applicableExclusions.forEach(rule => {
    log.debug(
      `Applying exclusion rule for ${fieldName}, excluding values:`,
      rule.excludedValues
    );
    rule.excludedValues.forEach(excludedValue => {
      const hadValue = filteredCounts.has(excludedValue);
      filteredCounts.delete(excludedValue);
      log.debug(
        `Excluded value "${excludedValue}" from ${fieldName} dropdown. Had value: ${hadValue}`
      );
    });
  });

  log.debug(
    `Original counts size: ${counts.size}, filtered counts size: ${filteredCounts.size}`
  );
  return filteredCounts;
}
